import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { getWhatsappPricing } from '../services/whatsappService';

/**
 * useWhatsappPricing
 *
 * Shared, read-only access to the global WhatsApp transport pricing
 * ({ enabled, templateCostEur, sessionMessageCostEur }, provider mitto).
 *
 * The config is global, so state is module-scoped and fetched once per
 * session: multiple consumers (channel card, config modal, template page,
 * flow node editors) reuse the same data without refetching. The read
 * endpoint lives under the bot scope, so workspaceId/botId are taken from
 * the current bot-editor route params.
 */

// Module-scoped shared cache.
const pricing = ref({ enabled: false, templateCostEur: 0, sessionMessageCostEur: 0 });
const loaded = ref(false);
let inflight = null;

export function useWhatsappPricing() {
  const route = useRoute();

  const fetchPricing = async () => {
    if (loaded.value) return pricing.value;
    if (inflight) return inflight;

    const workspaceId = route.params.workspaceId;
    const botId = route.params.id;
    if (!workspaceId || !botId) return pricing.value;

    inflight = (async () => {
      try {
        const res = await getWhatsappPricing(workspaceId, botId);
        if (res?.success && res.data) {
          pricing.value = {
            enabled: !!res.data.enabled,
            templateCostEur: Number(res.data.templateCostEur) || 0,
            sessionMessageCostEur: Number(res.data.sessionMessageCostEur) || 0,
          };
        }
        loaded.value = true;
      } catch (err) {
        Console.error('[useWhatsappPricing] fetch failed:', err);
      } finally {
        inflight = null;
      }
      return pricing.value;
    })();

    return inflight;
  };

  return { pricing, loaded, fetchPricing };
}
